// @ai-generated 2026-10-08T09:40:29+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
import {
    type ApiPlannedContainerAction,
    type ApiPlannedContainerActionGroup,
    EnumContainerOrderAction,
    getEnumContainerOrderActionLabel,
} from '@Model';
import { Badge, type Column, type LinkableField, LinkedCell, UnlinkedCell } from '@synqo/frontend-component-library';
import { actionChip, getVariantByActionType, isExchangeHalf } from '@utils/requisitionAction';

const dash = (text: string) => <>{text || '—'}</>;

const customerColumn: Readonly<Column<ApiPlannedContainerActionGroup>> = {
    name: 'customer.display_string',
    label: 'Kunde',
};

const siteColumn: Readonly<Column<ApiPlannedContainerActionGroup>> = {
    name: 'site.display_string',
    label: 'Standort',
};

const containerTypeColumn: Readonly<Column<ApiPlannedContainerActionGroup>> = {
    name: 'container_type.display_string',
    label: 'Containertyp',
};

const groupCountColumn: Readonly<Column<ApiPlannedContainerActionGroup>> = {
    name: 'count',
    label: 'Anzahl',
    disableColumnFilter: true,
    disableColumnSort: true,
};

const containerNumberColumn: Readonly<Column<ApiPlannedContainerAction>> = {
    name: 'container.display_string',
    label: 'Nummer',
    renderCell: ({ rowData }) => dash(rowData.container.display_string),
};

const countColumn: Readonly<Column<ApiPlannedContainerAction>> = {
    name: 'count',
    label: 'Anzahl',
    disableColumnFilter: true,
};

const contentColumn: Readonly<Column<ApiPlannedContainerAction>> = {
    name: 'content.display_string',
    label: 'Inhalt',
    renderCell: ({ rowData }) => dash(rowData.content.display_string),
};

const actionColumn: Readonly<Column<ApiPlannedContainerAction>> = {
    name: 'action',
    label: 'Aktion',
    disableColumnFilter: true,
    renderCell: ({ rowData }) => {
        if (isExchangeHalf(rowData)) {
            const { label, variant } = actionChip('swap');
            const half = rowData.action === EnumContainerOrderAction.Collect ? 'Abholung' : 'Stellung';

            return <Badge variant={variant}>{`${label} (${half})`}</Badge>;
        }

        return (
            <Badge variant={getVariantByActionType(rowData.action)}>
                {getEnumContainerOrderActionLabel(rowData.action)}
            </Badge>
        );
    },
};

const remarksColumn: Readonly<Column<ApiPlannedContainerAction>> = {
    name: 'remarks',
    label: 'Bemerkung',
    disableColumnSort: true,
    renderCell: ({ rowData }) => dash(rowData.remarks),
};

const orderColumn: Readonly<Column<ApiPlannedContainerAction>> = { name: 'source_number', label: 'Auftrag' };

/** The requisition of an action, as a link. */
// @ai-generated 2026-10-21T11:23:08+00:00 by niklas.eicker@tngtech.com using Claude Opus 5.5 (1M context)
function requisitionColumn(onOpen: (requisitionId: string) => void): Readonly<Column<ApiPlannedContainerAction>> {
    return {
        name: 'requisition_number',
        label: 'Bestellung',
        renderCell: ({ rowData }) => {
            const requisitionId = rowData.requisition_id;
            if (!requisitionId) {
                return <UnlinkedCell label="—" />;
            }

            return (
                <LinkedCell
                    label={rowData.requisition_number}
                    itemId={requisitionId}
                    onClick={() => onOpen(requisitionId)}
                />
            );
        },
    };
}

/** The columns of the groups of planned container actions. */
// @ai-generated 2026-10-22T12:10:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function plannedContainerActionColumns(
    containerTypeOf?: (containerType: LinkableField | undefined) => string,
): Readonly<Column<ApiPlannedContainerActionGroup>>[] {
    const typeColumn: Readonly<Column<ApiPlannedContainerActionGroup>> = containerTypeOf
        ? { ...containerTypeColumn, renderCell: ({ rowData }) => dash(containerTypeOf(rowData.container_type)) }
        : containerTypeColumn;

    return [customerColumn, siteColumn, typeColumn, groupCountColumn];
}

/**
 * The columns of the planned container actions a group is made of.
 *
 * @param onOpenRequisition Opens the requisition of an action; without it the column names the order.
 */
// @ai-generated 2026-10-22T12:10:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function plannedContainerActionLineColumns(
    onOpenRequisition?: (requisitionId: string) => void,
): Readonly<Column<ApiPlannedContainerAction>>[] {
    return [
        actionColumn,
        containerNumberColumn,
        countColumn,
        contentColumn,
        onOpenRequisition ? requisitionColumn(onOpenRequisition) : orderColumn,
        remarksColumn,
    ];
}
